import { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import TwoFactorSection from "@/components/TwoFactorSection";
import BrandingLogo from "@/components/BrandingLogo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Palette,
  Mail,
  ListChecks,
  Plus,
  Trash2,
  Save,
  ShieldCheck,
  ExternalLink,
  MapPin,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Copy,
  UserCircle2,
} from "lucide-react";

const DATE_FIELDS = [
  { value: "__none__", label: "No date required" },
  { value: "expiry_date", label: "Manual expiry date" },
  { value: "completed_date", label: "Date completed (auto-computes expiry)" },
  { value: "issue_date", label: "Issue date (auto-computes expiry)" },
];

export default function Settings() {
  const { refreshOrg, user, checkAuth } = useAuth();
  // Branding state
  const [org, setOrg] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#012E88");
  const [logoUrl, setLogoUrl] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [senderVerified, setSenderVerified] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);

  // Checklist state
  const [standardConfig, setStandardConfig] = useState(null);
  const [disabledItems, setDisabledItems] = useState([]);
  const [customItems, setCustomItems] = useState([]);
  const [savingCl, setSavingCl] = useState(false);

  const loadAll = useCallback(async () => {
    const [{ data: o }, { data: cfg }, { data: ov }] = await Promise.all([
      api.get("/orgs/current"),
      api.get("/config/roles"),
      api.get("/orgs/checklist"),
    ]);
    setOrg(o);
    setName(o.name || "");
    setColor(o.brand_primary_color || "#012E88");
    setLogoUrl(o.brand_logo_url || "");
    setSenderEmail(o.sender_email || "");
    setSenderVerified(!!o.sender_domain_verified);
    setStandardConfig(cfg);
    setDisabledItems(ov.disabled_items || []);
    setCustomItems((ov.custom_items || []).map((c) => ({
      ...c,
      applies_to_roles: c.applies_to_roles || [],
      _key: c._key || crypto.randomUUID(),
    })));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      const { data } = await api.patch("/orgs/current", {
        name: name.trim() || undefined,
        brand_primary_color: color,
        brand_logo_url: logoUrl.trim() || undefined,
        sender_email: senderEmail.trim() || undefined,
        sender_domain_verified: senderVerified,
      });
      setOrg(data);
      // Re-apply the brand colour globally so every open tab picks it up instantly.
      await refreshOrg();
      toast.success("Workspace branding saved");
    } catch (e) {
      toast.error("Could not save", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setSavingBrand(false);
    }
  };

  const toggleDisabled = (key) => {
    setDisabledItems((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const addCustomItem = () => {
    setCustomItems((prev) => [
      ...prev,
      { _key: crypto.randomUUID(), key: "", title: "", description: "", essential: true, date_field: "__none__", expiry_years: 0, applies_to_roles: [] },
    ]);
  };

  const updateCustom = (idx, patch) => {
    setCustomItems((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const removeCustom = (idx) => {
    setCustomItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveChecklist = async () => {
    // Validate custom items — strip client-only `_key` before sending.
    const cleaned = customItems.map((c) => ({
      key: (c.key || c.title || "").toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60),
      title: c.title.trim(),
      description: c.description || "",
      essential: !!c.essential,
      date_field: c.date_field === "__none__" ? null : c.date_field,
      expiry_years: Number(c.expiry_years) || 0,
      applies_to_roles: Array.isArray(c.applies_to_roles) ? c.applies_to_roles : [],
    }));
    if (cleaned.some((c) => !c.title || !c.key)) {
      toast.error("Every custom item needs a title.");
      return;
    }
    setSavingCl(true);
    try {
      const { data } = await api.put("/orgs/checklist", {
        disabled_items: disabledItems,
        custom_items: cleaned,
      });
      setDisabledItems(data.disabled_items);
      setCustomItems((data.custom_items || []).map((c) => ({
        ...c,
        applies_to_roles: c.applies_to_roles || [],
        _key: crypto.randomUUID(),
      })));
      toast.success("Checklist saved", { description: `${data.disabled_items.length} disabled · ${data.custom_items.length} custom` });
    } catch (e) {
      toast.error("Could not save", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setSavingCl(false);
    }
  };

  const standardItems = standardConfig?.standard_items || [];
  // Backend returns { worker_roles: [{ key, label, role_specific: [...] }] }.
  // Re-shape into { [role_key]: { label, items } } so we can render one group per role
  // and make it visually explicit which items belong to which role (e.g. NMC PIN → nurses only).
  const roleGroups = (standardConfig?.worker_roles || [])
    .map((r) => ({ key: r.key, label: r.label, items: r.role_specific || [] }))
    .filter((r) => r.items.length > 0);

  return (
    <AppLayout>
      <div>
        <div className="label-caps text-slate-500">Workspace</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
          Settings
        </h1>
        <p className="text-slate-600 mt-1.5 max-w-2xl">
          Customise how your workspace looks to your team, who sends the email
          notifications, and which compliance items you require.
        </p>
      </div>

      {/* --- Branding --- */}
      <section className="mt-10 rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-branding">
        <div className="flex items-center gap-2">
          <Palette className="h-4 w-4 text-teal-700" />
          <h2 className="font-heading text-xl font-bold">Branding</h2>
        </div>
        <p className="text-sm text-slate-600 mt-1">
          Shown in the sidebar and mobile header for every member of your workspace.
        </p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="label-caps">Workspace name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-org-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="label-caps">Primary colour</Label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value.toUpperCase())}
                data-testid="settings-color-picker"
                className="h-10 w-14 rounded-md border border-slate-200"
              />
              <Input value={color} onChange={(e) => setColor(e.target.value)} className="font-mono" />
            </div>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="label-caps">Logo</Label>
            <BrandingLogo
              logoUrl={logoUrl}
              logoPath={org?.brand_logo_path}
              onChanged={async (nextOrg) => {
                setOrg(nextOrg);
                setLogoUrl(nextOrg?.brand_logo_url || "");
                await refreshOrg();
              }}
            />
            <details className="mt-2">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 select-none">
                Or paste a hosted URL instead
              </summary>
              <div className="mt-2 space-y-1.5">
                <Input
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  data-testid="settings-logo-url"
                  placeholder="https://your-domain.com/logo.png"
                />
                <p className="text-xs text-slate-500">
                  If you host the file yourself. Uploading a file above wins over this URL.
                </p>
              </div>
            </details>
          </div>
        </div>
        <Button
          onClick={saveBrand}
          disabled={savingBrand}
          data-testid="settings-save-brand"
          className="mt-6 rounded-full h-10 px-6 bg-teal-700 hover:bg-teal-800"
        >
          <Save className="h-4 w-4 mr-1.5" /> {savingBrand ? "Saving…" : "Save branding"}
        </Button>
      </section>

      {/* --- Sender email --- */}
      <SenderSection senderEmail={senderEmail} setSenderEmail={setSenderEmail} onSaveBrand={saveBrand} savingBrand={savingBrand} />

      {/* --- Email signature --- */}
      <SignatureSection user={user} onSaved={checkAuth} />

      {/* --- Two-factor authentication --- */}
      <TwoFactorSection user={user} onChanged={checkAuth} />

      {/* --- Locations (sub-locations / homes / wards) --- */}
      <LocationsSection />

      {/* --- Custom checklist --- */}
      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-checklist">
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-teal-700" />
          <h2 className="font-heading text-xl font-bold">Compliance checklist</h2>
        </div>
        <p className="text-sm text-slate-600 mt-1">
          Turn off standard items you don&apos;t require, and add items unique to your organisation.
          Changes apply to every worker in your workspace.
        </p>

        <h3 className="mt-6 label-caps text-slate-500">Standard items</h3>
        <div className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded-md">
          {standardItems.map((it) => {
            const on = !disabledItems.includes(it.key);
            const locked = it.special_type === "right_to_work";
            return (
              <div key={it.key} className="flex items-start justify-between gap-4 px-4 py-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    {it.title}
                    {locked && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 text-[10px] uppercase tracking-widest px-2 py-0.5 text-slate-500">
                        <ShieldCheck className="h-3 w-3" /> Required by law
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{it.description}</div>
                </div>
                <Switch
                  checked={on}
                  disabled={locked}
                  onCheckedChange={() => toggleDisabled(it.key)}
                  data-testid={`toggle-standard-${it.key}`}
                />
              </div>
            );
          })}
        </div>

        {roleGroups.length > 0 && (
          <>
            <h3 className="mt-6 label-caps text-slate-500">Role-specific items</h3>
            <p className="text-xs text-slate-500 mt-1">
              These only apply to workers assigned to the role shown. For example, the NMC PIN
              check belongs to Registered Nurses only — Healthcare Assistants and other roles will
              never see it on their checklist.
            </p>
            <div className="mt-2 space-y-3">
              {roleGroups.map((group) => (
                <div key={group.key} className="border border-slate-200 rounded-md" data-testid={`role-group-${group.key}`}>
                  <div className="px-4 py-2 bg-slate-50 text-xs font-bold uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    {group.label} · only
                  </div>
                  <div className="divide-y divide-slate-100">
                    {group.items.map((it) => {
                      const on = !disabledItems.includes(it.key);
                      return (
                        <div key={it.key} className="flex items-start justify-between gap-4 px-4 py-3">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{it.title}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{it.description}</div>
                          </div>
                          <Switch
                            checked={on}
                            onCheckedChange={() => toggleDisabled(it.key)}
                            data-testid={`toggle-role-${it.key}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="mt-8 flex items-center justify-between">
          <h3 className="label-caps text-slate-500">Custom items ({customItems.length})</h3>
          <Button
            variant="outline"
            onClick={addCustomItem}
            data-testid="settings-add-custom"
            className="rounded-full h-9 px-4"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add custom item
          </Button>
        </div>

        <div className="mt-3 space-y-3">
          {customItems.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              No custom items yet. Add anything specific to your organisation — e.g. &ldquo;Company handbook signed&rdquo; or &ldquo;Annual appraisal&rdquo;.
            </div>
          )}
          {customItems.map((c, i) => (
            <div
              key={c._key || i}
              data-testid={`custom-item-${i}`}
              className="rounded-md border border-slate-200 bg-white p-4 grid gap-3 md:grid-cols-6"
            >
              <div className="md:col-span-2 space-y-1.5">
                <Label className="label-caps">Title</Label>
                <Input
                  value={c.title}
                  onChange={(e) => updateCustom(i, { title: e.target.value })}
                  data-testid={`custom-title-${i}`}
                  placeholder="Company handbook signed"
                />
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label className="label-caps">Description</Label>
                <Textarea
                  rows={2}
                  value={c.description}
                  onChange={(e) => updateCustom(i, { description: e.target.value })}
                  data-testid={`custom-description-${i}`}
                  placeholder="Signed acknowledgement of the company handbook"
                />
              </div>
              <div className="md:col-span-1 space-y-1.5">
                <Label className="label-caps">Essential</Label>
                <div className="h-10 flex items-center">
                  <Switch
                    checked={c.essential}
                    onCheckedChange={(v) => updateCustom(i, { essential: v })}
                    data-testid={`custom-essential-${i}`}
                  />
                </div>
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label className="label-caps">Date behavior</Label>
                <Select
                  value={c.date_field || "__none__"}
                  onValueChange={(v) => updateCustom(i, { date_field: v })}
                >
                  <SelectTrigger data-testid={`custom-date-field-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_FIELDS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(c.date_field === "completed_date" || c.date_field === "issue_date") && (
                <div className="md:col-span-2 space-y-1.5">
                  <Label className="label-caps">Expires N years later</Label>
                  <Input
                    type="number"
                    min={0}
                    max={10}
                    value={c.expiry_years}
                    onChange={(e) => updateCustom(i, { expiry_years: e.target.value })}
                    data-testid={`custom-expiry-years-${i}`}
                  />
                </div>
              )}
              <div className="md:col-span-1 flex items-end justify-end">
                <Button
                  variant="ghost"
                  onClick={() => removeCustom(i)}
                  data-testid={`custom-remove-${i}`}
                  className="text-rose-600 hover:text-rose-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="md:col-span-6 space-y-1.5 border-t border-slate-100 pt-3">
                <Label className="label-caps">Applies to</Label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => updateCustom(i, { applies_to_roles: [] })}
                    data-testid={`custom-role-all-${i}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                      (c.applies_to_roles || []).length === 0
                        ? "bg-teal-700 text-white border-teal-700"
                        : "bg-white text-slate-700 border-slate-300 hover:border-teal-400"
                    }`}
                  >
                    All roles
                  </button>
                  {(standardConfig?.worker_roles || []).map((r) => {
                    const active = (c.applies_to_roles || []).includes(r.key);
                    return (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => {
                          const current = c.applies_to_roles || [];
                          const next = active
                            ? current.filter((k) => k !== r.key)
                            : [...current, r.key];
                          updateCustom(i, { applies_to_roles: next });
                        }}
                        data-testid={`custom-role-${r.key}-${i}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                          active
                            ? "bg-teal-700 text-white border-teal-700"
                            : "bg-white text-slate-700 border-slate-300 hover:border-teal-400"
                        }`}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">
                  {(c.applies_to_roles || []).length === 0
                    ? "Shown on every worker's checklist."
                    : `Only shown to: ${(c.applies_to_roles || [])
                        .map((k) => (standardConfig?.worker_roles || []).find((r) => r.key === k)?.label || k)
                        .join(", ")}.`}
                </p>
              </div>
            </div>
          ))}
        </div>

        <Button
          onClick={saveChecklist}
          disabled={savingCl}
          data-testid="settings-save-checklist"
          className="mt-6 rounded-full h-10 px-6 bg-teal-700 hover:bg-teal-800"
        >
          <Save className="h-4 w-4 mr-1.5" /> {savingCl ? "Saving…" : "Save checklist"}
        </Button>
      </section>
    </AppLayout>
  );
}


function SenderSection({ senderEmail, setSenderEmail, onSaveBrand, savingBrand }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orgs/sender/domain");
      setStatus(data);
    } catch (e) {
      setStatus({ error: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [planBlocked, setPlanBlocked] = useState(false);

  const addToResend = async () => {
    if (!status?.domain) {
      toast.error("Save a sender email first (e.g. noreply@yourdomain.co.uk)");
      return;
    }
    setAdding(true);
    setPlanBlocked(false);
    try {
      await api.post("/orgs/sender/domain", { domain: status.domain });
      toast.success("Domain added to Resend", { description: "Copy the DNS records into your registrar." });
      load();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      // 409 = server-side plan-limit translation from routes_orgs._resend_call
      if (e.response?.status === 409) {
        setPlanBlocked(true);
        toast.info("Custom domain unavailable on this plan", { description: detail });
      } else {
        toast.error("Could not add domain", { description: detail });
      }
    } finally {
      setAdding(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/orgs/sender/domain/verify");
      if (data.verified) toast.success("Domain verified — emails will send from your domain now.");
      else toast.info(`Status: ${data.status}. DNS may still be propagating.`);
      load();
    } catch (e) {
      toast.error("Verification failed", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setVerifying(false);
    }
  };

  const copyRecord = async (v) => {
    try {
      await navigator.clipboard.writeText(v);
      toast.success("Copied");
    } catch (_e) {
      /* ignore */
    }
  };

  const verified = status?.verified;
  const notAdded = status?.status === "not_added";

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-sender">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-teal-700" />
          <h2 className="font-heading text-xl font-bold">Email sender</h2>
        </div>
        {status?.domain && (
          <span
            className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border ${
              verified
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-amber-50 text-amber-800 border-amber-200"
            }`}
            data-testid="settings-sender-status"
          >
            {verified ? "Verified" : status.status || "Pending"}
          </span>
        )}
      </div>
      <p className="text-sm text-slate-600 mt-1">
        By default, your invites and rejection emails send from{" "}
        <span className="font-mono text-slate-900">noreply@audentra.co.uk</span> with your
        team member on the Reply-To. To send from your own domain, paste it below and add
        the DNS records to your registrar.
      </p>

      {planBlocked && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="settings-sender-plan-blocked">
          <div className="font-semibold">Custom domain unavailable on the current plan</div>
          <div className="text-amber-800 mt-0.5">
            Your workspace is already sending fine from <span className="font-mono">noreply@audentra.co.uk</span> —
            replies still route to your team members. Contact Audentra support if you need
            email to come from your own domain and we&apos;ll upgrade the plan.
          </div>
        </div>
      )}

      <div className="mt-6 space-y-1.5">
        <Label className="label-caps">Sender email</Label>
        <div className="flex gap-2">
          <Input
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            data-testid="settings-sender-email"
            placeholder="noreply@yourcompany.co.uk"
          />
          <Button
            onClick={onSaveBrand}
            disabled={savingBrand}
            data-testid="settings-save-sender"
            className="rounded-full h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white"
          >
            Save
          </Button>
        </div>
        <p className="text-xs text-slate-500">Save the address first, then set up its DNS records below.</p>
      </div>

      {status?.domain && (
        <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm text-slate-700">
              Resend domain: <span className="font-mono font-semibold text-slate-900">{status.domain}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={load}
                disabled={loading}
                data-testid="settings-sender-refresh"
                className="h-8"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              </Button>
              {notAdded && (
                <Button size="sm" onClick={addToResend} disabled={adding} className="h-8 bg-teal-700 hover:bg-teal-800" data-testid="settings-sender-add">
                  {adding ? "Adding…" : "Add to Resend"}
                </Button>
              )}
              {!notAdded && !verified && (
                <Button size="sm" onClick={verify} disabled={verifying} className="h-8 bg-teal-700 hover:bg-teal-800" data-testid="settings-sender-verify">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {verifying ? "Checking…" : "Re-check DNS"}
                </Button>
              )}
            </div>
          </div>

          {status.records && status.records.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="settings-sender-records">
              <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Add these DNS records</div>
              {status.records.map((r, i) => (
                <div key={i} className="rounded-md border border-slate-200 bg-white p-3 text-xs">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-block rounded-full px-2 py-0.5 bg-slate-100 border border-slate-200 font-mono text-[10px] uppercase">{r.record}</span>
                      {r.status && (
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border text-[10px] uppercase font-semibold ${
                            r.status === "verified"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-amber-50 text-amber-800 border-amber-200"
                          }`}
                        >
                          {r.status === "verified" ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                          {r.status}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 grid gap-1">
                    <RecordRow label="Name" value={r.name} onCopy={copyRecord} />
                    <RecordRow label="Type" value={r.type} onCopy={copyRecord} />
                    <RecordRow label="Value" value={r.value} onCopy={copyRecord} />
                    {r.ttl && <RecordRow label="TTL" value={String(r.ttl)} onCopy={copyRecord} />}
                    {r.priority !== undefined && r.priority !== null && (
                      <RecordRow label="Priority" value={String(r.priority)} onCopy={copyRecord} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {status.error && (
            <div className="mt-3 text-xs text-rose-700">{status.error}</div>
          )}

          <div className="mt-3 text-xs text-slate-500">
            <a
              href="https://resend.com/domains"
              target="_blank"
              rel="noreferrer"
              className="text-teal-700 hover:underline inline-flex items-center gap-1"
            >
              Manage on Resend <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      )}
    </section>
  );
}

function RecordRow({ label, value, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-500 w-16 shrink-0">{label}</span>
      <code className="font-mono text-slate-900 truncate flex-1 bg-slate-50 border border-slate-200 rounded px-2 py-1">{value}</code>
      <button
        type="button"
        onClick={() => onCopy(value)}
        className="h-6 w-6 rounded hover:bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"
        aria-label="Copy"
      >
        <Copy className="h-3 w-3" />
      </button>
    </div>
  );
}

function SignatureSection({ user, onSaved }) {
  const [sigName, setSigName] = useState("");
  const [sigPhone, setSigPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setSigName(user.signature_name || "");
      setSigPhone(user.signature_phone || "");
    }
  }, [user?.signature_name, user?.signature_phone, user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me", {
        signature_name: sigName,
        signature_phone: sigPhone,
      });
      await onSaved?.();
      toast.success("Signature saved", { description: "It'll appear on every invite email you send." });
    } catch (e) {
      toast.error("Could not save", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setSaving(false);
    }
  };

  const displayName = (sigName || user?.name || "").trim();

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-signature">
      <div className="flex items-center gap-2">
        <UserCircle2 className="h-4 w-4 text-teal-700" />
        <h2 className="font-heading text-xl font-bold">Invite email signature</h2>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Add a personal sign-off so new joiners see who invited them. Appears at the
        bottom of every invite email you send from Audentra.
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="label-caps">Sign-off name</Label>
          <Input
            value={sigName}
            onChange={(e) => setSigName(e.target.value)}
            data-testid="settings-signature-name"
            placeholder={user?.name || "e.g. Dave Adams, Registered Manager"}
          />
          <p className="text-xs text-slate-500">Leave blank to use your account name ({user?.name}).</p>
        </div>
        <div className="space-y-1.5">
          <Label className="label-caps">Phone (optional)</Label>
          <Input
            value={sigPhone}
            onChange={(e) => setSigPhone(e.target.value)}
            data-testid="settings-signature-phone"
            placeholder="+44 7700 900123"
          />
        </div>
      </div>

      {(displayName || sigPhone) && (
        <div className="mt-5 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
          <div className="label-caps text-slate-500 mb-2">Preview</div>
          <div className="text-sm text-slate-600">Thanks,</div>
          <div className="text-slate-900 font-semibold mt-0.5" data-testid="signature-preview-name">{displayName}</div>
          {sigPhone && <div className="text-slate-600 text-sm mt-0.5" data-testid="signature-preview-phone">{sigPhone}</div>}
        </div>
      )}

      <Button
        onClick={save}
        disabled={saving}
        data-testid="settings-save-signature"
        className="mt-6 rounded-full h-10 px-6 bg-teal-700 hover:bg-teal-800"
      >
        <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save signature"}
      </Button>
    </section>
  );
}

function LocationsSection() {
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("home");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/locations");
      setLocations(data);
    } catch (_e) {
      /* ignore */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) {
      toast.error("Give the location a name");
      return;
    }
    setBusy(true);
    try {
      await api.post("/locations", { name: name.trim(), kind });
      setName("");
      toast.success("Location added");
      load();
    } catch (e) {
      toast.error("Could not add", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Workers assigned here will become unassigned.`)) return;
    try {
      await api.delete(`/locations/${id}`);
      toast.success("Location deleted");
      load();
    } catch (e) {
      toast.error("Could not delete", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-locations">
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-teal-700" />
        <h2 className="font-heading text-xl font-bold">Locations</h2>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Group workers by home, ward or site — the manager dashboard can then be filtered by location.
      </p>

      <div className="mt-4 grid gap-2 md:grid-cols-[1fr_180px_auto]">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Oakwood House"
          data-testid="settings-location-name"
        />
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger data-testid="settings-location-kind"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="home">Home</SelectItem>
            <SelectItem value="ward">Ward</SelectItem>
            <SelectItem value="team">Team</SelectItem>
            <SelectItem value="site">Site</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={add}
          disabled={busy}
          className="bg-teal-700 hover:bg-teal-800 rounded-full h-10 px-4"
          data-testid="settings-location-add"
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      <div className="mt-4 divide-y divide-slate-100 border border-slate-200 rounded-md">
        {locations.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-slate-500">No locations yet.</div>
        )}
        {locations.map((l) => (
          <div key={l.id} data-testid={`settings-location-${l.id}`} className="flex items-center justify-between gap-2 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">{l.name}</div>
              <div className="text-xs text-slate-500 mt-0.5 uppercase tracking-widest">
                {l.kind} · {l.worker_count} worker{l.worker_count === 1 ? "" : "s"}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => remove(l.id, l.name)}
              data-testid={`settings-location-remove-${l.id}`}
              className="text-rose-600 hover:text-rose-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}
